import { Tooltip } from 'antd';
import React from 'react';
import { InfoCircleOutlined, CopyOutlined, WarningOutlined } from '@ant-design/icons';
import Colors from '../constants/Colors';
import { sortByLocalCompare } from '../helpers/helpers';
import TagRender from '../common/TagRender';
import OntraccrPopconfirm from '../common/modals/OntraccrPopconfirm';
import { resolveFormDivisionIds } from './formHelpers';

export default {};

export const AUTO_COLLECTED_ID = 'autoCollected';
export const collectedDataMap = {
  employeeId: { title: 'Employee ID', id: 'employeeId' },
  employeeName: { title: 'Employee Name', id: 'employeeName' },
  employeeSignature: { title: 'Collect Signature', id: 'employeeSignature' },
  date: { title: 'Submission Date', id: 'date' },
  number: { title: 'Form Number', id: 'number' },
};

export const INTEGRATIONS_ID = 'integrations';

export const CURRENCIES = [
  { value: 'cad', label: 'CAD' },
  { value: 'usd', label: 'USD' },
];

export const priceToMarkupMap = {
  price: 'markup',
  labourPrice: 'labourMarkup',
};

export const priceToCostMap = {
  price: 'cost',
  labourPrice: 'labourCost',
};

export const TABLE_TYPES_WITH_COLUMN_PERMISSIONS = new Set([
  'TimeEntry',
  'Materials',
  'Equipment',
  'Custom',
]);

export const dataTypesAllowedInExternalForm = new Set([
  'Customers',
  'Projects',
  'Users',
  'Equipment',
  'Vendors',
  'Costcodes',
  'Contacts',
  'Custom',
  'Labels',
  'Buckets',
  'Sub-Contracts',
  'PayableSubContracts',
]);

export const dataTypesAllowedInMultiDivisionDropdown = new Set([
  'Projects',
  'Users',
]);

export const dataTypesAllowedInMultiDivisionTable = new Set([
  'TimeEntry',
  'Equipment',
  'Materials',
]);

export const MULTI_DIVISION_ALLOWED_WORKFLOW_ACTION_TYPES = new Set([
  'logicalCondition',
  'logicalYesNo',
  'email',
  'createTime',
  'updateEquipment',
  'push',
  'updateCost',
  'sageIntacctPO',
]);

export const MULTI_DIVISION_ALLOWED_WORKFLOW_INFRASTRUCTURE_TYPES = new Set([
  'user',
  'approval',
  'edit',
  'complete',
  'default',
  'trigger',
  'external',
]);

export const MULTI_DIVISION_INVALID_FIELD_MESSAGE = 'This field is not supported in multi-division forms.';

export const MULTI_DIVISION_INVALID_WORKFLOW_STEP_ERROR = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This workflow step is not supported in multi-division forms.
    </div>
  ),
};

export const MULTI_DIVISION_SWITCH_MODAL_COPY = `Please note that certain fields are not supported in multi-division forms.
Please remove unsupported fields and workflow steps in the form before saving.
`;

const getPendingTableColumnRender = (record) => {
  const { pendingTableValue, pendingTableFormName } = record;
  if (!pendingTableValue) return null;
  return (
    <Tooltip title={`This row is pending from a form awaiting approval: ${pendingTableFormName}`} placement="top">
      <span style={{ cursor: 'pointer', color: 'red' }}>
        <InfoCircleOutlined />
      </span>
    </Tooltip>
  );
};

export const pendingTableColumn = {
  title: '',
  dataIndex: '',
  width: 50,
  render: (_, record) => getPendingTableColumnRender(record),
  setRender: (record) => (_, tableRecord) => getPendingTableColumnRender(record ?? tableRecord),
};

export const getConflictTableColumn = ({ conflictMap = {}, onConflictClick }) => ({
  title: '',
  dataIndex: '',
  width: 50,
  key: 'conflict-warning',
  setRender: (record) => (_, tableRecord) => {
    const currentRecord = record ?? tableRecord;
    if (currentRecord?.isUserHeader || !conflictMap[currentRecord?.id]) return null;
    return (
      <WarningOutlined
        style={{ color: Colors.ONTRACCR_RED, cursor: 'pointer' }}
        onClick={(e) => {
          e.stopPropagation();
          onConflictClick(currentRecord);
        }}
      />
    );
  }
});

const stopProp = (e) => e.stopPropagation();

export const getFormTemplateDisplayColumns = ({
  useRedesign,
  divisions,
  hasWritePerms,
  onCopy,
}) => {
  const cols = [
    {
      title: 'Name',
      dataIndex: 'name',
      width: 200,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByLocalCompare('name'),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 100,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      sorter: sortByLocalCompare('type'),
    },
  ];
  if (Object.keys(divisions).length !== 1) {
    cols.push({
      title: 'Divisions',
      dataIndex: 'divisionId',
      width: 150,
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
      render: (divisionId, record) => (
        <TagRender
          tagMap={divisions}
          tagIds={resolveFormDivisionIds({ divisionIds: record.divisionIds, divisionId })}
          useRedesign={useRedesign}
        />
      ),
    });
  }

  if (hasWritePerms) {
    cols.push({
      title: '',
      dataIndex: 'copy',
      width: 20,
      render: (_, record) => (
        <OntraccrPopconfirm
          placement='rightTop'
          title={`Create a copy of ${record.name}?`}
          onConfirm={(e) => onCopy(e, record)}
          onCancel={stopProp}
          trigger='click'
        >
          <CopyOutlined onClick={stopProp} />
        </OntraccrPopconfirm>
      ),
    });
  }

  return cols;
};
